import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withCustomMetrics } from '@zocdoc/frontend-metrics';
import { usePhoneAuthentication } from './hooks/usePhoneAuthentication';
import { useResendVerificationCode } from './hooks/useResendVerificationCode';
import { MFAVerificationFormView } from './MFAVerificationFormView';

/**
 * MFA Verification Form Container
 * @param {boolean} email - needed for authentication
 * @param {boolean} isInMobileMenu
 * @param {function} onSuccessfulAuthentication - dispatches SIGN_IN_SUCCESS to complete login
 * @param {function} redirectLockedUser - sets users redirectUrl from api response to redux state
 * @param {AUTH_PASSWORD_RESPONSE_BODY} responseBody - returned by successful POST to:
 *  '/accounts/v2/authentication/authenticate-password'
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {boolean} signedIn
 * @param {string} suggestedRedirect
 * @returns {JSX.Element|null} MFAVerificationFormContainer
 */

const MFAVerificationFormWrapper = ({
    email,
    isInMobileMenu = false,
    onSuccessfulAuthentication,
    redirectLockedUser,
    responseBody,
    sendMetric,
    signedIn,
    suggestedRedirect,
}) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isRequestingCode, setIsRequestingCode] = useState(false);
    const [isCodeResent, setIsCodeResent] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [verificationCodeDestination, setVerificationCodeDestination] =
        useState(responseBody?.verificationCodeDestination);
    const [verificationId, setVerificationId] = useState(
        responseBody?.verificationId
    );
    const [verificationToken, setVerificationToken] = useState(
        responseBody?.verificationToken
    );
    const [shouldDisableForm, setShouldDisableForm] = useState(false);

    const isAuthSuccessful = useRef(false);
    const isLockedUser = useRef(false);
    const apiResponseBody = useRef(null);

    useEffect(() => {
        if (signedIn && isInMobileMenu) {
            window.location.assign(suggestedRedirect);
        }
    }, [signedIn, suggestedRedirect, isInMobileMenu]);

    const handleChange = useCallback(
        ({ target }) => {
            setVerificationCode(target.value);
            setErrorMessage('');
        },
        [setErrorMessage, setVerificationCode]
    );

    const handleResendCode = useResendVerificationCode({
        email,
        errorMessage,
        sendMetric,
        setErrorMessage,
        setIsCodeResent,
        setIsRequestingCode,
        setShouldDisableForm,
        verificationId,
        verificationToken,
        setVerificationCodeDestination,
        setVerificationId,
        setVerificationToken,
    });

    const handleSubmit = usePhoneAuthentication({
        apiResponseBody,
        email,
        errorMessage,
        isAuthSuccessful,
        isLockedUser,
        sendMetric,
        setIsAuthenticating,
        setErrorMessage,
        setShouldDisableForm,
        setVerificationCode,
        verificationId,
        verificationCode,
        verificationToken,
    });

    if (isLockedUser.current) {
        redirectLockedUser(apiResponseBody.current);
    }

    if (isAuthSuccessful.current) {
        onSuccessfulAuthentication(apiResponseBody.current);
    }

    return (
        <MFAVerificationFormView
            errorMessage={errorMessage}
            handleChange={handleChange}
            handleResendCode={handleResendCode}
            handleSubmit={handleSubmit}
            isAuthenticating={isAuthenticating}
            isCodeResent={isCodeResent}
            isRequestingCode={isRequestingCode}
            shouldDisableForm={shouldDisableForm}
            verificationCodeDestination={verificationCodeDestination}
            verificationCode={verificationCode}
        />
    );
};

export const MFAVerificationFormContainer = withCustomMetrics({
    section: 'MFAVerificationForm',
    component: 'MFAVerificationForm',
})(MFAVerificationFormWrapper);
